import type { EntityType, FieldType } from "@/schemas/digital-files";
import type { RegistryType } from "@/schemas/digital-files/registry";
import type { Section } from "../types";

export const getSectionsFromEntity = (data: EntityType) => {
  const sections: Section[] = [];
  if (data?.fields) {
    for (const element of data.fields) {
      const sectionTitle = element.configuration?.section?.title || "";
      const sectionOrder = element.configuration?.section?.order || 0;

      if (sections.some((sec) => sec.title === sectionTitle)) {
        const sectionIndex = sections.findIndex(
          (sec) => sec.title === sectionTitle,
        );
        sections[sectionIndex].fields.push(element);
      } else {
        sections.push({
          id: crypto.randomUUID(),
          title: sectionTitle,
          order: sectionOrder,
          fields: [element],
        });
      }
    }
  }
  return sections;
};

export const getSectionsFromRegistry = (data: RegistryType) => {
  const sections: Section[] = [];
  if (data?.field_values) {
    for (const element of data.field_values) {
      const sectionTitle =
        element.entity_field?.configuration?.section?.title || "";
      const sectionOrder =
        element.entity_field?.configuration?.section?.order || 0;

      if (sections.some((sec) => sec.title === sectionTitle)) {
        const sectionIndex = sections.findIndex(
          (sec) => sec.title === sectionTitle,
        );
        sections[sectionIndex].fields.push(element.entity_field as FieldType);
      } else {
        sections.push({
          id: crypto.randomUUID(),
          title: sectionTitle,
          order: sectionOrder,
          fields: [element.entity_field as FieldType],
        });
      }
    }
  }
  return sections;
};

export const getMappedFields = (sections: Section[]): FieldType[] => {
  return sections.flatMap((section, sectionIndex) =>
    section.fields.map((field, fieldIndex) => {
      const {
        id,
        name,
        type,
        field_type,
        is_required,
        default_value,
        configuration,
        is_array,
        max_items,
      } = field;

      // Build configuration object and filter out null values
      const configurationObject = {
        show_in_summary: configuration?.show_in_summary || false,
        section: {
          title: section.title,
          order: sectionIndex + 1,
        },
        key: configuration?.key,
        buttonPosition: configuration?.buttonPosition,
        placeholder: configuration?.placeholder,
        maxLength: configuration?.maxLength,
        buttonEnabled: configuration?.buttonEnabled ?? false,
        // Dropdown button: solo enviar si buttonEnabled es true
        ...(configuration?.buttonEnabled && {
          options_source: configuration?.options_source,
          ...(configuration?.options_source === "database" && {
            options_table: configuration?.options_table,
            options_column: configuration?.options_column,
          }),
          ...(configuration?.options_source === "manual" &&
            configuration?.options_dropdown && {
              options_dropdown: configuration.options_dropdown,
            }),
        }),
        // Campo select/local_list: según options_root_source, enviar tabla/columna O options
        ...(configuration?.options_root_source === "database" && {
          options_root_table: configuration?.options_root_table,
          options_root_column: configuration?.options_root_column,
        }),
        ...(configuration?.options_root_source === "manual" &&
          configuration?.options && {
            options: configuration?.options,
          }),
        options_root_source: configuration?.options_root_source,
        multipleSelection: configuration?.multipleSelection,
        can_expand: configuration?.can_expand,
        type: configuration?.type || type,
        decimals: configuration?.decimals,
        minDate: configuration?.minDate,
        maxDate: configuration?.maxDate,
        includeTime: configuration?.includeTime,
        format24: configuration?.format24,
        allowedDomains: configuration?.allowedDomains,
        noAllowedDomains: configuration?.noAllowedDomains,
        allowCountryNumber: configuration?.allowCountryNumber,
        documentType: configuration?.documentType,
        allowedFileTypes: configuration?.allowedFileTypes,
        allowMultipleFiles: configuration?.allowMultipleFiles,
        maxFileSize: configuration?.maxFileSize,
        ocrFile: configuration?.ocrFile,
        dataFile: configuration?.dataFile,
        interopInstitution: configuration?.interopInstitution,
        interopAction: configuration?.interopAction,
      };

      // Filter out null and undefined values, but preserve empty arrays for domain validations
      const filteredConfiguration = Object.fromEntries(
        Object.entries(configurationObject).filter(([key, value]) => {
          // Always keep allowedDomains and noAllowedDomains even if empty
          if (key === "allowedDomains" || key === "noAllowedDomains") {
            return Array.isArray(value);
          }
          return value != null;
        }),
      );

      return {
        id: id,
        name: name,
        field_type: field_type,
        is_required: is_required,
        default_value: default_value,
        configuration: filteredConfiguration,
        order: fieldIndex + 1,
        is_array: is_array,
        max_items: max_items,
      };
    }),
  );
};
